import { useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { PageHeader, Card, CardTitle, colors, fonts } from '../components/UI'

// Content written per stop-slop skill: active voice, specific, no filler, reader-first
const GLOSSARY = [
  { term: 'APR', category: 'Debt', definition: 'Annual Percentage Rate. The yearly cost of borrowing, including fees. A credit card at 24.9% APR costs you £249 for every £1,000 you carry for a year.' },
  { term: 'Avalanche method', category: 'Debt', definition: 'Pay minimum on all debts, throw every spare pound at the highest APR debt first. Costs you less in interest than snowball, but takes longer to clear your first debt.' },
  { term: 'Budget', category: 'Budgeting', definition: 'A plan for where your money goes each month. You set limits per category before spending, not after. Without one, spending fills available space.' },
  { term: 'Cash ISA', category: 'Savings', definition: 'A savings account where interest is tax-free. You can deposit up to £20,000 per tax year. The interest rate is usually lower than a Stocks & Shares ISA long-term.' },
  { term: 'Compound interest', category: 'Investing', definition: 'Earning interest on your interest. £1,000 at 7% grows to £1,070 after year one, then £1,145 after year two. Over 30 years it becomes £7,612 without adding a penny.' },
  { term: 'Debt-to-income ratio', category: 'Debt', definition: 'Your total monthly debt payments divided by your monthly income. A ratio above 40% is a warning sign lenders and financial advisers watch closely.' },
  { term: 'Emergency fund', category: 'Savings', definition: 'Cash set aside for unexpected costs - job loss, car breakdown, boiler failure. Three to six months of expenses is the standard target. Keep it in an easy-access savings account, not invested.' },
  { term: 'Equity', category: 'Investing', definition: 'Ownership stake in something. In a house it\'s the value minus the mortgage. In a company it\'s shares. Building equity means your ownership stake is worth more over time.' },
  { term: 'Financial freedom', category: 'Investing', definition: 'The point where your assets generate enough income to cover your living costs without working. For most people this means a large invested portfolio, a paid-off home, or both.' },
  { term: 'Index fund', category: 'Investing', definition: 'A fund that tracks a market index like the S&P 500 or FTSE 100. Low fees, automatic diversification. Warren Buffett recommends them for most investors.' },
  { term: 'ISA', category: 'Savings', definition: 'Individual Savings Account. UK wrapper that shelters your money from tax. Types include Cash ISA, Stocks & Shares ISA, and Lifetime ISA. The annual allowance is £20,000.' },
  { term: 'Lifetime ISA', category: 'Savings', definition: 'A government-boosted ISA for first-time buyers or retirement. You get a 25% bonus on contributions up to £4,000 per year. Withdrawals for other purposes carry a penalty.' },
  { term: 'Minimum payment', category: 'Debt', definition: 'The smallest amount a lender will accept each month. Paying only the minimum on a £3,000 credit card at 24.9% APR takes over 27 years to clear and costs more than £4,500 in interest.' },
  { term: 'Net worth', category: 'Budgeting', definition: 'Everything you own minus everything you owe. £50,000 in savings and a £200,000 mortgage on a £250,000 house gives you £100,000 net worth.' },
  { term: 'Pension', category: 'Investing', definition: 'A long-term savings pot for retirement with tax relief. For every £80 you put in, the government adds £20 (basic rate). Many employers also contribute. It\'s tax-advantaged investing.' },
  { term: 'Savings rate', category: 'Budgeting', definition: 'The percentage of your take-home pay you save or invest each month. 20% is a solid target. At 50% you can reach financial independence in under 17 years.' },
  { term: 'Snowball method', category: 'Debt', definition: 'Pay minimum on all debts, attack the smallest balance first regardless of interest rate. You clear debts faster, get quick wins, and many people find it easier to stick to than avalanche.' },
  { term: 'Stocks & Shares ISA', category: 'Investing', definition: 'An ISA that holds investments rather than cash. Returns are tax-free. Over 20+ years, a well-diversified Stocks & Shares ISA typically outperforms a Cash ISA by a significant margin.' },
  { term: 'SIPP', category: 'Investing', definition: 'Self-Invested Personal Pension. A pension you control yourself, choosing your own investments. Suited to self-employed people or those who want more flexibility than a workplace pension.' },
].sort((a, b) => a.term.localeCompare(b.term))

const CONTENT = [
  {
    category: 'Getting Started',
    icon: 'ti-rocket',
    items: [
      {
        title: 'Where to start with your money',
        summary: 'Most people skip straight to investing before sorting the basics. Here\'s the order that actually makes sense.',
        url: 'https://theinvestingcouple.co.uk/money-guides/where-to-start-with-your-money/',
        readTime: '4 min',
      },
      {
        title: 'How to build a budget that sticks',
        summary: 'Zero-based budgeting, 50/30/20, envelope method - the differences explained, and how to pick one.',
        url: 'https://theinvestingcouple.co.uk/money-guides/how-to-build-a-budget-that-sticks/',
        readTime: '5 min',
      },
      {
        title: 'Net worth - why it\'s the number that matters',
        summary: 'Income tells you what you earn. Net worth tells you where you stand. How to calculate yours and what to do with the number.',
        url: 'https://theinvestingcouple.co.uk/money-guides/net-worth-why-it-matters/',
        readTime: '3 min',
      },
    ]
  },
  {
    category: 'Managing Debt',
    icon: 'ti-credit-card',
    items: [
      {
        title: 'Avalanche vs snowball - which pays off debt faster',
        summary: 'Avalanche saves more money. Snowball keeps more people motivated. The maths and the psychology, side by side.',
        url: 'https://theinvestingcouple.co.uk/money-guides/avalanche-vs-snowball/',
        readTime: '5 min',
      },
      {
        title: 'The real cost of minimum payments',
        summary: 'A £3,000 credit card balance at 24.9% APR takes 27 years to clear on minimum payments. Here\'s what you\'re actually paying.',
        url: 'https://theinvestingcouple.co.uk/money-guides/the-real-cost-of-minimum-payments/',
        readTime: '3 min',
      },
      {
        title: 'Good debt vs bad debt',
        summary: 'A mortgage and a payday loan are both debt. The difference between borrowing that builds wealth and borrowing that destroys it.',
        url: 'https://theinvestingcouple.co.uk/money-guides/good-debt-vs-bad-debt/',
        readTime: '4 min',
      },
    ]
  },
  {
    category: 'Building Savings',
    icon: 'ti-piggy-bank',
    items: [
      {
        title: 'Why your emergency fund comes before investing',
        summary: 'Three to six months of expenses in cash before you put a penny in the market. Here\'s the reasoning.',
        url: 'https://theinvestingcouple.co.uk/money-guides/why-your-emergency-fund-comes-before-investing/',
        readTime: '3 min',
      },
      {
        title: 'ISAs explained - Cash vs Stocks & Shares',
        summary: 'Both are tax-free wrappers. The right one depends on your timeline. A clear breakdown with no jargon.',
        url: 'https://theinvestingcouple.co.uk/money-guides/isas-explained-cash-vs-stocks-and-shares/',
        readTime: '5 min',
      },
      {
        title: 'How to hit a 20% savings rate',
        summary: 'Twenty percent sounds big. With the right order of operations it\'s more achievable than most people think.',
        url: 'https://theinvestingcouple.co.uk/money-guides/how-to-hit-a-20-percent-savings-rate/',
        readTime: '4 min',
      },
    ]
  },
  {
    category: 'Ready to Invest?',
    icon: 'ti-trending-up',
    items: [
      {
        title: 'The financial checklist before you start investing',
        summary: 'Emergency fund? Debt under control? Budget solid? If you can tick these off, you\'re ready.',
        url: 'https://theinvestingcouple.co.uk/money-guides/the-financial-checklist-before-you-start-investing/',
        readTime: '3 min',
      },
      {
        title: 'Index funds - the simplest way to start',
        summary: 'What they are, why fees matter more than you think, and how to buy your first one.',
        url: 'https://theinvestingcouple.co.uk/money-guides/index-funds-the-simplest-way-to-start/',
        readTime: '6 min',
      },
      {
        title: 'Pension vs ISA - where to put money first',
        summary: 'Both are tax-advantaged. Employer matching changes the calculation. The order of priority explained.',
        url: 'https://theinvestingcouple.co.uk/money-guides/pension-vs-isa-where-to-put-money-first/',
        readTime: '5 min',
      },
    ]
  },
]

const CATEGORIES = ['All', ...new Set(GLOSSARY.map(g => g.category))]

export default function LearnPage({ defaultTab }) {
  const { profile } = useAuth()
  const [tab, setTab] = useState(defaultTab || 'guides')
  const [glossaryFilter, setGlossaryFilter] = useState('All')
  const [glossarySearch, setGlossarySearch] = useState('')
  const [expandedTerm, setExpandedTerm] = useState(null)
  const hintsEnabled = profile?.hints_enabled !== false

  const filteredGlossary = GLOSSARY.filter(g => {
    const matchCat = glossaryFilter === 'All' || g.category === glossaryFilter
    const matchSearch = g.term.toLowerCase().includes(glossarySearch.toLowerCase())
    return matchCat && matchSearch
  })

  return (
    <div style={{ paddingBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '18px 20px 4px', position: 'relative' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text }}>Learn</div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>Guides, glossary and resources</div>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: '2px', padding: '16px 16px 0', borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
        {[{ id: 'appguide', label: 'App guide' }, { id: 'guides', label: 'Guides' }, { id: 'glossary', label: 'Glossary' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '8px 20px', fontSize: '14px', fontFamily: fonts.body, fontWeight: 600,
            letterSpacing: '0.01em', border: 'none', cursor: 'pointer',
            background: 'transparent', borderBottom: tab === t.id ? `2px solid ${colors.gold}` : '2px solid transparent',
            color: tab === t.id ? colors.gold : colors.muted, marginBottom: '-1px',
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ padding: '16px' }}>

        {/* GUIDES */}
  
      {/* ── App Guide tab ──────────────────────────────────────────── */}
      {tab === 'appguide' && (
        <div style={{ padding: '16px' }}>
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '20px', marginBottom: '12px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.gold, marginBottom: '10px' }}>ABOUT THIS APP</div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, lineHeight: 1.7 }}>
              TIC Tracker is a pay-period budgeting app built around how you actually get paid. Everything - Safe to Spend, bills, budget, goals - resets and recalculates at the start of each new period.
            </div>
          </div>

          <div style={{ background: 'rgba(201,151,43,0.06)', border: '1px solid rgba(201,151,43,0.2)', borderRadius: '14px', padding: '20px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: colors.text, marginBottom: '6px' }}>Full guide to using the tracker</div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, lineHeight: 1.6, marginBottom: '16px' }}>
              Everything you need to know - Safe to Spend, bills, budget, goals, accounts, insights and more. Detailed walkthroughs for every feature.
            </div>
            <a href="https://theinvestingcouple.co.uk/tracker/guide/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', background: colors.gold, color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, padding: '10px 20px', borderRadius: '10px', letterSpacing: '0.01em' }}>
              Open guide ↗
            </a>
          </div>
        </div>
      )}

      {tab === 'guides' && (
          <div>
            {CONTENT.map(section => (
              <div key={section.category} style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <i className={`ti ${section.icon}`} style={{ fontSize: '16px', color: colors.gold }} />
                  <span style={{ fontSize: '16px', color: colors.gold, letterSpacing: '0.1em', fontFamily: fonts.body, fontWeight: 700 }}>{section.category}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {section.items.map(item => (
                    <a key={item.title} href={item.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                      <div style={{
                        background: colors.card, border: `1px solid rgba(255,255,255,0.06)`,
                        borderRadius: '12px', padding: '14px 16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
                        transition: 'border-color 0.15s',
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 500, marginBottom: '4px' }}>{item.title}</div>
                          <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, lineHeight: 1.5 }}>{item.summary}</div>
                          <div style={{ fontSize: '16px', color: colors.mutedDeep, fontFamily: fonts.body, marginTop: '6px' }}>{item.readTime} read</div>
                        </div>
                        <i className="ti ti-arrow-right" style={{ fontSize: '16px', color: colors.gold, flexShrink: 0 }} />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}

            {/* Starting Line CTA */}
            <div style={{
              background: `linear-gradient(135deg, rgba(201,151,43,0.08) 0%, rgba(26,39,68,0.5) 100%)`,
              border: '1px solid rgba(255,255,255,0.07)',
              borderRadius: '12px', padding: '20px',
              marginTop: '8px',
            }}>
              <div style={{ fontSize: '14px', color: colors.gold, letterSpacing: '0.04em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '6px' }}>The Investing Couple</div>
              <div style={{ fontSize: '16px', color: colors.text, fontFamily: fonts.heading, fontWeight: 700, letterSpacing: '0.04em', marginBottom: '8px' }}>Ready to start investing?</div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, lineHeight: 1.6, marginBottom: '16px' }}>
                The Starting Line is our beginner investing course. Built for people who want to go from financially sorted to actually investing - with confidence.
              </div>
              <a href="https://theinvestingcouple.co.uk/starting-line/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', background: colors.gold, color: colors.onAccent, fontSize: '14px', fontFamily: fonts.body, fontWeight: 700, padding: '10px 20px', borderRadius: '10px', letterSpacing: '0.01em' }}>
                Find out more <i className="ti ti-arrow-right" style={{ fontSize: '16px' }} />
              </a>
            </div>
          </div>
        )}

        {/* GLOSSARY */}
        {tab === 'glossary' && (
          <div>
            {/* Search */}
            <div style={{ position: 'relative', marginBottom: '12px' }}>
              <i className="ti ti-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: colors.muted, fontSize: '16px' }} />
              <input
                value={glossarySearch}
                onChange={e => setGlossarySearch(e.target.value)}
                placeholder="Search terms..."
                style={{ width: '100%', padding: '10px 12px 10px 36px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '10px', color: colors.text, fontSize: '16px', fontFamily: fonts.body, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Category filter */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => setGlossaryFilter(cat)} style={{
                  padding: '4px 12px', fontSize: '16px', fontFamily: fonts.body, fontWeight: 600,
                  letterSpacing: '0.06em', border: `1px solid ${glossaryFilter === cat ? colors.gold : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '10px', cursor: 'pointer', background: glossaryFilter === cat ? 'rgba(201,151,43,0.1)' : 'transparent',
                  color: glossaryFilter === cat ? colors.gold : colors.muted,
                }}>{cat}</button>
              ))}
            </div>

            {/* Terms */}
            {filteredGlossary.map(g => (
              <div key={g.term} style={{ borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                <button onClick={() => setExpandedTerm(expandedTerm === g.term ? null : g.term)} style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 0', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '16px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>{g.term}</span>
                    <span style={{ fontSize: '14px', color: colors.muted, background: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '2px 6px', fontFamily: fonts.body, letterSpacing: '0.06em' }}>{g.category}</span>
                  </div>
                  <i className={`ti ti-chevron-${expandedTerm === g.term ? 'up' : 'down'}`} style={{ fontSize: '16px', color: colors.muted, flexShrink: 0 }} />
                </button>
                {expandedTerm === g.term && (
                  <div style={{ padding: '0 0 14px', fontSize: '14px', fontWeight: 500, color: colors.muted, fontFamily: fonts.body, lineHeight: 1.7 }}>
                    {g.definition}
                  </div>
                )}
              </div>
            ))}

            {filteredGlossary.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: colors.muted, fontFamily: fonts.body, fontSize: '14px' }}>No terms match your search</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
